import "@/App.css";
import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { AnimatePresence } from "framer-motion";
import { Toaster } from "sonner";
import { AppProvider } from "@/context/AppContext";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
const Home = lazy(() => import("@/pages/Home"));
import ScrollToTop from "@/components/ScrollToTop";

const Shop = lazy(() => import("@/pages/Shop"));
const ProductDetail = lazy(() => import("@/pages/ProductDetail"));
const Cart = lazy(() => import("@/pages/Cart"));
const Checkout = lazy(() => import("@/pages/Checkout"));
const Wishlist = lazy(() => import("@/pages/Wishlist"));
const About = lazy(() => import("@/pages/About"));
const Contact = lazy(() => import("@/pages/Contact"));
const Login = lazy(() => import("@/pages/Login"));
const Register = lazy(() => import("@/pages/Register"));
const Account = lazy(() => import("@/pages/Account"));
const OrderSuccess = lazy(() => import("@/pages/OrderSuccess"));
const Blog = lazy(() => import("@/pages/Blog"));
const BlogPost = lazy(() => import("@/pages/BlogPost"));
const AdminLayout = lazy(() => import("@/pages/admin/AdminLayout"));
const AdminDashboard = lazy(() => import("@/pages/admin/AdminDashboard"));
const AdminProducts = lazy(() => import("@/pages/admin/AdminProducts"));
const AdminProductForm = lazy(() => import("@/pages/admin/AdminProductForm"));
const AdminOrders = lazy(() => import("@/pages/admin/AdminOrders"));
const AdminProfile = lazy(() => import("@/pages/admin/AdminProfile"));
const AdminCategories = lazy(() => import("@/pages/admin/AdminCategories"));
const AdminHome = lazy(() => import("@/pages/admin/AdminHome"));
const AdminCoupons = lazy(() => import("@/pages/admin/AdminCoupons"));
const AdminBlog = lazy(() => import("@/pages/admin/AdminBlog"));
const AdminReels = lazy(() => import("@/pages/admin/AdminReels"));
const AdminUsers = lazy(() => import("@/pages/admin/AdminUsers"));
const AdminSellers = lazy(() => import("@/pages/admin/AdminSellers"));
const AdminReturns = lazy(() => import("@/pages/admin/AdminReturns"));
const AdminSettlement = lazy(() => import("@/pages/admin/AdminSettlement"));
const AdminOfflineSales = lazy(() => import("@/pages/admin/AdminOfflineSales"));
const AuthCallback = lazy(() => import("@/pages/AuthCallback"));
const ForgotPassword = lazy(() => import("@/pages/ForgotPassword"));
const ResetPassword = lazy(() => import("@/pages/ResetPassword"));

function RouteFallback() {
  return (
    <div
      aria-live="polite"
      className="min-h-[40vh] flex items-center justify-center font-playfair text-xl text-yw-gray"
    >
      Loading…
    </div>
  );
}

function AnimatedRoutes() {
  const location = useLocation();

  return (
    <Suspense fallback={<RouteFallback />}>
      <AnimatePresence mode="wait">
        <Routes location={location} key={location.pathname}>
          <Route path="/" element={<Home />} />
          <Route path="/shop" element={<Shop />} />
          <Route path="/shop/:category" element={<Shop />} />
          <Route path="/product/:slug" element={<ProductDetail />} />
          <Route path="/cart" element={<Cart />} />
          <Route path="/checkout" element={<Checkout />} />
          <Route path="/wishlist" element={<Wishlist />} />
          <Route path="/about" element={<About />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/blog" element={<Blog />} />
          <Route path="/blog/:slug" element={<BlogPost />} />
          <Route path="/account" element={<Account />} />
          <Route path="/order-success/:orderId" element={<OrderSuccess />} />

          <Route path="/admin" element={<AdminLayout />}>
            <Route index element={<AdminDashboard />} />
            <Route path="products" element={<AdminProducts />} />
            <Route path="products/new" element={<AdminProductForm />} />
            <Route path="products/:id/edit" element={<AdminProductForm />} />
            <Route path="orders" element={<AdminOrders />} />
            <Route path="categories" element={<AdminCategories />} />
            <Route path="home" element={<AdminHome />} />
            <Route path="coupons" element={<AdminCoupons />} />
            <Route path="blog" element={<AdminBlog />} />
            <Route path="reels" element={<AdminReels />} />
            <Route path="users" element={<AdminUsers />} />
            <Route path="sellers" element={<AdminSellers />} />
            <Route path="returns" element={<AdminReturns />} />
            <Route path="settlement" element={<AdminSettlement />} />
            <Route path="offline-sales" element={<AdminOfflineSales />} />
            <Route path="profile" element={<AdminProfile />} />
          </Route>

          <Route path="/auth/callback" element={<AuthCallback />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
        </Routes>
      </AnimatePresence>
    </Suspense>
  );
}

export default function App() {
  return (
    <AppProvider>
      <BrowserRouter>
        <ScrollToTop />

        <div className="min-h-screen bg-yw-bg text-yw-ink">
          <Navbar />
          <AnimatedRoutes />
          <Footer />
        </div>

        <Toaster
          position="bottom-right"
          toastOptions={{ style: { fontFamily: "Inter, sans-serif" } }}
        />
      </BrowserRouter>
    </AppProvider>
  );
}
